/**
 * Destination Localization Review — field-by-field generate, translate, and staleness.
 *
 * States (per locale field):
 * - Missing: no translation yet
 * - Stale: translation exists but English changed since last translate/accept
 * - Current: translation is tied to the current English (via source hash)
 *
 * “Accept as current” keeps the translation text and updates the EN hash
 * (same as former “mark validated”) — use when the wording is still fine.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  EntityCustomViewParams,
  PropertyFieldBinding,
  useAuthController,
  useSnackbarController,
  type Property,
} from "@firecms/core";
import { Button, Markdown, Paper, Typography } from "@firecms/ui";
import { getApps } from "firebase/app";
import { doc, getFirestore, serverTimestamp, updateDoc } from "firebase/firestore";
import { CONTENT_STATUS } from "../lib/contentStatus";
import {
  applyPublishedSnapshot,
  hasPublishedSnapshot,
} from "../lib/publishedSnapshot";

const CONTENT_ENGINE_URL = import.meta.env.VITE_CONTENT_ENGINE_URL?.replace(/\/$/, "") ?? "";

const LOCALES = ["en", "de", "fr", "it"] as const;
type Locale = (typeof LOCALES)[number];
const TARGET_LOCALES = ["de", "fr", "it"] as const;
type TargetLocale = (typeof TARGET_LOCALES)[number];

const CONTENT_FIELDS = [
  "headline",
  "intro",
  "stats",
  "faqs",
  "tldr",
  "page_title",
  "meta_title",
  "meta_description",
] as const;
type ContentField = (typeof CONTENT_FIELDS)[number];

const FIELD_LABELS: Record<ContentField, string> = {
  headline: "Headline",
  intro: "Intro",
  stats: "Stats",
  faqs: "FAQs",
  tldr: "At a glance",
  page_title: "Page title",
  meta_title: "Meta title",
  meta_description: "Meta description",
};

const LOCALE_LABELS: Record<Locale, string> = {
  en: "English (Master)",
  de: "German",
  fr: "French",
  it: "Italian",
};

type ContentBlock = {
  headline?: string;
  intro?: string;
  stats?: string[];
  faqs?: Array<{ q?: string; a?: string }>;
  tldr?: string[];
  page_title?: string;
  meta_title?: string;
  meta_description?: string;
};

type LocaleStatus = {
  locale: TargetLocale;
  has_content: boolean;
  stale: boolean;
  stale_fields: ContentField[];
  empty_fields: ContentField[];
  translated_at?: string;
};

type FieldLocaleState = "current" | "stale" | "missing" | "empty_en";

function fieldPreview(block: ContentBlock | undefined, field: ContentField): string {
  if (!block) return "";
  if (field === "headline") return String(block.headline ?? "");
  if (field === "intro") return String(block.intro ?? "");
  if (field === "stats") return Array.isArray(block.stats) ? block.stats.join("\n") : "";
  if (field === "tldr") return Array.isArray(block.tldr) ? block.tldr.map((b) => `• ${b}`).join("\n") : "";
  if (field === "page_title") return String(block.page_title ?? "");
  if (field === "meta_title") return String(block.meta_title ?? "");
  if (field === "meta_description") return String(block.meta_description ?? "");
  if (Array.isArray(block.faqs)) {
    return block.faqs.map((f) => `Q: ${f.q ?? ""}\nA: ${f.a ?? ""}`).join("\n\n");
  }
  return "";
}

function fieldLocaleState(
  field: ContentField,
  st: LocaleStatus | undefined,
  enEmpty: boolean,
): FieldLocaleState {
  if (enEmpty) return "empty_en";
  if (!st) return "missing";
  if (st.empty_fields?.includes(field)) return "missing";
  if (st.stale_fields?.includes(field)) return "stale";
  return "current";
}

function StateBadge({ state }: { state: FieldLocaleState }) {
  const styles: Record<FieldLocaleState, string> = {
    current: "bg-green-100 text-green-900",
    stale: "bg-orange-100 text-orange-900",
    missing: "bg-amber-100 text-amber-900",
    empty_en: "bg-gray-100 text-gray-600",
  };
  const labels: Record<FieldLocaleState, string> = {
    current: "Current",
    stale: "Stale vs EN",
    missing: "Missing",
    empty_en: "No EN",
  };
  return (
    <span className={`text-xs px-1.5 py-0.5 rounded font-medium whitespace-nowrap ${styles[state]}`}>
      {labels[state]}
    </span>
  );
}

async function postJson(
  path: string,
  token: string,
  body: Record<string, unknown>,
): Promise<{ ok: boolean; status: number; data: Record<string, unknown> }> {
  const res = await fetch(`${CONTENT_ENGINE_URL}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  return { ok: res.ok, status: res.status, data };
}

export function DestinationLocalizationReview({
  values,
  formContext,
  statusPropertyKey,
  statusProperty,
  entityId,
}: {
  values: Record<string, unknown>;
  formContext: EntityCustomViewParams<Record<string, unknown>>["formContext"];
  statusPropertyKey: string;
  statusProperty: Property;
  entityId?: string;
}) {
  const snackbar = useSnackbarController();
  const authController = useAuthController();
  const [status, setStatus] = useState<LocaleStatus[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const content = (values.content ?? {}) as Record<string, ContentBlock | undefined>;
  const currentStatus = (values[statusPropertyKey] as string) ?? "draft";
  const canMarkReady =
    currentStatus === "draft" ||
    currentStatus === "unpublished" ||
    currentStatus === "archived";
  const canMarkLiveReady = currentStatus === "published_draft" || currentStatus === "published";
  const canRevert =
    hasPublishedSnapshot(values) &&
    (currentStatus === "published_draft" || currentStatus === "published_ready");

  const getAuthToken = useCallback(
    () => authController.getAuthToken(),
    [authController],
  );

  const refreshStatus = useCallback(async () => {
    if (!entityId || !CONTENT_ENGINE_URL) return;
    const token = await getAuthToken();
    if (!token?.trim()) return;
    const res = await postJson("/api/destinations/translation-status", token, {
      document_id: entityId,
    });
    if (res.ok && Array.isArray(res.data.status)) {
      setStatus(res.data.status as LocaleStatus[]);
    }
  }, [entityId, getAuthToken]);

  useEffect(() => {
    void refreshStatus();
  }, [refreshStatus]);

  const withAuth = async (label: string, fn: (token: string) => Promise<void>) => {
    if (!entityId) {
      snackbar.open({ type: "error", message: "Save the destination before running actions." });
      return;
    }
    if (!CONTENT_ENGINE_URL) {
      snackbar.open({ type: "error", message: "VITE_CONTENT_ENGINE_URL is not configured." });
      return;
    }
    const token = await getAuthToken();
    if (!token?.trim()) {
      snackbar.open({ type: "error", message: "Not authenticated. Please sign in again." });
      return;
    }
    setBusy(label);
    try {
      await fn(token);
      await refreshStatus();
    } catch (err) {
      snackbar.open({
        type: "error",
        message: err instanceof Error ? err.message : "Request failed",
      });
    } finally {
      setBusy(null);
    }
  };

  const regenerateField = (field: ContentField | "keywords" | "research_brief") =>
    withAuth(`gen-${field}`, async (token) => {
      const res = await postJson("/run-destination-expert", token, {
        document_id: entityId,
        fields: [field],
      });
      if (!res.ok) {
        snackbar.open({ type: "error", message: String(res.data.error ?? "Generate failed") });
        return;
      }
      snackbar.open({
        type: res.data.skipped ? "info" : "success",
        message: res.data.skipped
          ? `Skipped ${field}`
          : `Regenerated ${field}. Reload the entity to see updated English.`,
      });
    });

  const translate = (opts: {
    mode: "force" | "fill_empty" | "stale_only";
    fields?: ContentField[];
    locales?: TargetLocale[];
  }) =>
    withAuth(`tr-${opts.mode}-${(opts.fields ?? []).join("-")}`, async (token) => {
      const res = await postJson("/translate", token, {
        document_id: entityId,
        mode: opts.mode,
        fields: opts.fields,
        locales: opts.locales,
      });
      if (!res.ok) {
        snackbar.open({ type: "error", message: String(res.data.error ?? "Translate failed") });
        return;
      }
      snackbar.open({
        type: res.data.skipped ? "info" : "success",
        message: String(res.data.message ?? "Translation complete. Reload to see updates."),
      });
    });

  /** Accept existing translation text as matching current EN (updates source hash; no rewrite). */
  const acceptAsCurrent = (opts: { fields?: ContentField[]; locales?: TargetLocale[] }) =>
    withAuth(`accept-${(opts.fields ?? []).join("-")}`, async (token) => {
      const res = await postJson("/mark-translation-validated", token, {
        document_id: entityId,
        fields: opts.fields,
        locales: opts.locales,
        notes: "Accepted as current without re-translation",
      });
      if (!res.ok) {
        snackbar.open({ type: "error", message: String(res.data.error ?? "Accept failed") });
        return;
      }
      snackbar.open({
        type: "success",
        message: String(res.data.message ?? "Accepted as current"),
      });
    });

  const saveStatus = async (nextStatus: string, successMessage: string) => {
    const previousStatus = values[statusPropertyKey];
    formContext.setFieldValue(statusPropertyKey, nextStatus);
    try {
      await formContext.save({
        ...values,
        [statusPropertyKey]: nextStatus,
      } as Record<string, unknown>);
      snackbar.open({ type: "success", message: successMessage });
    } catch (err) {
      formContext.setFieldValue(statusPropertyKey, previousStatus);
      snackbar.open({
        type: "error",
        message: err instanceof Error ? err.message : "Could not update status",
      });
    }
  };

  const handleMarkReady = () =>
    void saveStatus(
      "ready_for_publishing",
      "Marked Ready for Publishing. Use Publish in the top bar to put this live.",
    );

  const handleMarkLiveReady = () =>
    void saveStatus(
      "published_ready",
      `Marked ${CONTENT_STATUS.published_ready}. Use Publish in the top bar to update the live page.`,
    );

  const handleRevert = async () => {
    const snap = values.published_snapshot;
    if (!entityId || !snap || typeof snap !== "object") {
      snackbar.open({ type: "error", message: "No live snapshot to revert to." });
      return;
    }
    const app = getApps()[0];
    if (!app) {
      snackbar.open({ type: "error", message: "Firebase is not initialized." });
      return;
    }
    const restored = applyPublishedSnapshot(values, snap as Record<string, unknown>);
    const { status: _s, updated_at: _u, created_at: _c, ...fields } = restored;
    try {
      await updateDoc(doc(getFirestore(app), "destinations", entityId), {
        ...fields,
        status: "published",
        updated_at: serverTimestamp(),
      });
      snackbar.open({
        type: "success",
        message: "Reverted to the live version. Reload the entity to see it.",
      });
      await refreshStatus();
    } catch (err) {
      snackbar.open({
        type: "error",
        message: err instanceof Error ? err.message : "Revert failed",
      });
    }
  };

  const statusByLocale = useMemo(
    () => new Map((status ?? []).map((s) => [s.locale, s])),
    [status],
  );

  const anyMissing = useMemo(
    () => (status ?? []).some((s) => (s.empty_fields?.length ?? 0) > 0),
    [status],
  );
  const anyStale = useMemo(
    () => (status ?? []).some((s) => (s.stale_fields?.length ?? 0) > 0),
    [status],
  );

  const fieldNeeds = useMemo(() => {
    const out = {} as Record<
      ContentField,
      { missing: boolean; stale: boolean; translateable: boolean }
    >;
    for (const field of CONTENT_FIELDS) {
      const enEmpty = !fieldPreview(content.en, field).trim();
      let missing = false;
      let stale = false;
      for (const locale of TARGET_LOCALES) {
        const state = fieldLocaleState(field, statusByLocale.get(locale), enEmpty);
        if (state === "missing") missing = true;
        if (state === "stale") stale = true;
      }
      out[field] = { missing, stale, translateable: missing || stale };
    }
    return out;
  }, [content.en, statusByLocale]);

  const staleSummary = useMemo(() => {
    if (!status) return null;
    return TARGET_LOCALES.map((locale) => {
      const st = statusByLocale.get(locale);
      const stale = st?.stale_fields ?? [];
      const missing = st?.empty_fields ?? [];
      return { locale, stale, missing };
    });
  }, [status, statusByLocale]);

  return (
    <div className="flex flex-col gap-6 p-4 min-w-0 overflow-x-hidden">
      <Paper className="p-4 flex flex-wrap items-center justify-between gap-4 min-w-0">
        <div className="flex flex-col gap-2 min-w-0">
          <Typography variant="label" className="font-medium">
            Document status
          </Typography>
          <div className="flex items-center gap-3 flex-wrap">
            <PropertyFieldBinding
              propertyKey={statusPropertyKey}
              property={statusProperty}
              context={formContext}
              minimalistView
            />
            {canMarkReady && (
              <Button
                variant="filled"
                color="primary"
                onClick={() => void handleMarkReady()}
                aria-label="Mark ready for publishing"
              >
                Mark Ready for Publishing
              </Button>
            )}
            {canMarkLiveReady && (
              <Button
                variant="filled"
                color="primary"
                onClick={() => void handleMarkLiveReady()}
                aria-label="Mark live ready"
              >
                Mark Live · Ready
              </Button>
            )}
            {canRevert && (
              <Button
                variant="outlined"
                disabled={!!busy}
                onClick={() => void handleRevert()}
                aria-label="Revert to live version"
              >
                Revert to live
              </Button>
            )}
          </div>
          {(currentStatus === "published_draft" || currentStatus === "published_ready") && (
            <Typography variant="caption" className="text-gray-600">
              This page is still on the live site from the last Publish. CMS edits stay here until
              you Publish again (or revert).
            </Typography>
          )}
        </div>
      </Paper>

      <Paper className="p-4 flex flex-col gap-3 min-w-0">
        <Typography variant="subtitle2" className="font-semibold">
          Translation status (vs English)
        </Typography>
        <Typography variant="body2" className="text-gray-600">
          <strong>Current</strong> is tied to the current English. <strong>Stale vs EN</strong> means
          English changed after the last translate/accept. <strong>Missing</strong> has no
          translation yet. Use <strong>Accept as current</strong> when the existing translation is
          still fine (no rewrite).
        </Typography>
        {!status && (
          <Typography variant="caption" className="text-gray-500">
            {CONTENT_ENGINE_URL
              ? "Loading translation status…"
              : "Set VITE_CONTENT_ENGINE_URL to load stale/current status."}
          </Typography>
        )}
        {staleSummary && (
          <div className="overflow-x-auto min-w-0">
            <table className="w-full text-sm border-collapse table-fixed">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 pr-2 font-medium w-[22%]">Field</th>
                  {TARGET_LOCALES.map((locale) => (
                    <th key={locale} className="py-2 pr-2 font-medium w-[26%]">
                      {LOCALE_LABELS[locale]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {CONTENT_FIELDS.map((field) => {
                  const enEmpty = !fieldPreview(content.en, field).trim();
                  return (
                    <tr key={field} className="border-b border-gray-100 dark:border-surface-700">
                      <td className="py-2 pr-2 font-medium">{FIELD_LABELS[field]}</td>
                      {TARGET_LOCALES.map((locale) => {
                        const state = fieldLocaleState(
                          field,
                          statusByLocale.get(locale),
                          enEmpty,
                        );
                        return (
                          <td key={`${field}-${locale}`} className="py-2 pr-2">
                            <StateBadge state={state} />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {staleSummary && (
          <ul className="text-sm text-gray-600 list-disc list-inside">
            {staleSummary.map(({ locale, stale, missing }) => (
              <li key={locale}>
                <strong>{LOCALE_LABELS[locale]}:</strong>{" "}
                {stale.length === 0 && missing.length === 0
                  ? "all current"
                  : [
                      stale.length ? `stale ${stale.map((f) => FIELD_LABELS[f]).join(", ")}` : null,
                      missing.length
                        ? `missing ${missing.map((f) => FIELD_LABELS[f]).join(", ")}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            size="small"
            variant="filled"
            color="primary"
            disabled={!!busy}
            onClick={() => translate({ mode: "force" })}
          >
            Translate all
          </Button>
          {anyMissing && (
            <Button
              size="small"
              variant="outlined"
              disabled={!!busy}
              onClick={() => translate({ mode: "fill_empty" })}
            >
              Fill untranslated
            </Button>
          )}
          {anyStale && (
            <Button
              size="small"
              variant="outlined"
              disabled={!!busy}
              onClick={() => translate({ mode: "stale_only" })}
            >
              Re-translate stale
            </Button>
          )}
          {anyStale && (
            <Button
              size="small"
              variant="text"
              disabled={!!busy}
              onClick={() => acceptAsCurrent({})}
            >
              Accept stale as current
            </Button>
          )}
          <Button size="small" variant="text" disabled={!!busy} onClick={() => void refreshStatus()}>
            Refresh status
          </Button>
        </div>
        {busy && (
          <Typography variant="caption" className="text-gray-500">
            Working: {busy}…
          </Typography>
        )}
      </Paper>

      <Paper className="p-4 flex flex-col gap-3 min-w-0">
        <Typography variant="subtitle2" className="font-semibold">
          Editorial research (English, not published)
        </Typography>
        <Typography variant="body2" className="text-gray-600">
          Internal research stack used to generate public fields. Regenerating the brief runs draft →
          challenge → revise. Only the <strong>final research brief</strong> drives public generation.
          It does not overwrite public fields unless you also Generate all English.
          Generate from existing brief: use field buttons below (they use the saved final brief as context).
        </Typography>
        {(typeof values.research_draft === "string" && values.research_draft.trim()) ? (
          <details className="text-sm">
            <summary className="cursor-pointer font-medium">Research draft (debugging)</summary>
            <pre className="mt-2 whitespace-pre-wrap text-xs bg-gray-50 text-gray-900 dark:bg-surface-800 dark:text-gray-100 p-2 rounded max-h-48 overflow-auto">
              {String(values.research_draft)}
            </pre>
          </details>
        ) : null}
        {(typeof values.research_challenge === "string" && values.research_challenge.trim()) ? (
          <details className="text-sm">
            <summary className="cursor-pointer font-medium">Challenge notes (draft audit)</summary>
            <pre className="mt-2 whitespace-pre-wrap text-xs bg-gray-50 text-gray-900 dark:bg-surface-800 dark:text-gray-100 p-2 rounded max-h-48 overflow-auto">
              {String(values.research_challenge)}
            </pre>
          </details>
        ) : null}
        {(typeof values.research_brief === "string" && values.research_brief.trim()) ? (
          <details className="text-sm">
            <summary className="cursor-pointer font-medium">Final research brief (used for generation)</summary>
            <pre className="mt-2 whitespace-pre-wrap text-xs bg-gray-50 text-gray-900 dark:bg-surface-800 dark:text-gray-100 p-2 rounded max-h-48 overflow-auto">
              {String(values.research_brief)}
            </pre>
          </details>
        ) : (
          <Typography variant="caption" className="text-amber-800">
            No final research brief yet — generate before or with public content.
          </Typography>
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            size="small"
            variant="filled"
            color="primary"
            disabled={!!busy}
            onClick={() => regenerateField("research_brief")}
          >
            Regenerate research brief stack
          </Button>
        </div>
      </Paper>

      <Paper className="p-4 flex flex-col gap-3 min-w-0">
        <Typography variant="subtitle2" className="font-semibold">
          Generate English field-by-field
        </Typography>
        <Typography variant="body2" className="text-gray-600">
          Regenerates only the selected English field. Translations for that field become stale
          until you re-translate or accept them as current.{" "}
          <strong>Generate all English</strong> keeps keywords and images;{" "}
          <strong>Generate all English + images</strong> also refreshes keywords and the gallery.
        </Typography>
        <div className="flex flex-wrap gap-2">
          {CONTENT_FIELDS.map((field) => (
            <Button
              key={field}
              size="small"
              variant="filled"
              color="primary"
              disabled={!!busy}
              onClick={() => regenerateField(field)}
            >
              Generate {FIELD_LABELS[field]}
            </Button>
          ))}
          <Button
            size="small"
            variant="outlined"
            disabled={!!busy}
            onClick={() => regenerateField("keywords")}
          >
            Generate keywords
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={!!busy}
            onClick={() =>
              withAuth("gen-all", async (token) => {
                const res = await postJson("/run-destination-expert", token, {
                  document_id: entityId,
                  force: true,
                  refetch_images: false,
                });
                if (!res.ok) {
                  snackbar.open({
                    type: "error",
                    message: String(res.data.error ?? "Generate failed"),
                  });
                  return;
                }
                const changed = res.data.content_changed === true;
                snackbar.open({
                  type: changed ? "success" : "info",
                  message: changed
                    ? `Regenerated English (${
                      Array.isArray(res.data.changed_fields)
                        ? (res.data.changed_fields as string[]).join(", ")
                        : "fields updated"
                    }). Status refreshed — Stale locales need re-translate. Reload entity for EN text.`
                    : "Generate finished but English text is unchanged. Translations correctly stay Current.",
                });
              })
            }
          >
            Generate all English
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={!!busy}
            onClick={() =>
              withAuth("gen-all-images", async (token) => {
                const res = await postJson("/run-destination-expert", token, {
                  document_id: entityId,
                  force: true,
                  refetch_images: true,
                });
                if (!res.ok) {
                  snackbar.open({
                    type: "error",
                    message: String(res.data.error ?? "Generate failed"),
                  });
                  return;
                }
                const changed = res.data.content_changed === true;
                snackbar.open({
                  type: changed ? "success" : "info",
                  message: changed
                    ? "Regenerated English, keywords, and images. Status refreshed — re-translate Stale locales. Reload for EN text."
                    : "Generate finished but English text is unchanged. Translations correctly stay Current.",
                });
              })
            }
          >
            Generate all English + images
          </Button>
        </div>
      </Paper>

      {CONTENT_FIELDS.map((field) => {
        const needs = fieldNeeds[field];
        return (
          <Paper key={field} className="p-4 flex flex-col gap-3 min-w-0 overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 min-w-0">
              <Typography variant="subtitle2" className="font-semibold">
                {FIELD_LABELS[field]}
              </Typography>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="small"
                  variant="filled"
                  disabled={!!busy}
                  onClick={() => regenerateField(field)}
                >
                  Generate EN
                </Button>
                {needs.translateable && (
                  <Button
                    size="small"
                    variant="text"
                    disabled={!!busy}
                    onClick={() => translate({ mode: "force", fields: [field] })}
                  >
                    Translate field
                  </Button>
                )}
                {needs.missing && (
                  <Button
                    size="small"
                    variant="text"
                    disabled={!!busy}
                    onClick={() => translate({ mode: "fill_empty", fields: [field] })}
                  >
                    Fill empty locales
                  </Button>
                )}
                {needs.stale && (
                  <Button
                    size="small"
                    variant="text"
                    disabled={!!busy}
                    onClick={() => translate({ mode: "stale_only", fields: [field] })}
                  >
                    Re-translate stale
                  </Button>
                )}
                {needs.stale && (
                  <Button
                    size="small"
                    variant="text"
                    disabled={!!busy}
                    onClick={() => acceptAsCurrent({ fields: [field] })}
                  >
                    Accept as current
                  </Button>
                )}
              </div>
            </div>

            <div
              className="grid gap-3 localization-review-grid w-full min-w-0"
              style={{
                gridTemplateColumns: `repeat(${LOCALES.length}, minmax(0, 1fr))`,
              }}
            >
              {LOCALES.map((locale) => {
                const block = content[locale];
                const text = fieldPreview(block, field);
                const st = locale === "en" ? undefined : statusByLocale.get(locale as TargetLocale);
                const enEmpty = !fieldPreview(content.en, field).trim();
                const state =
                  locale === "en" ? null : fieldLocaleState(field, st, enEmpty);
                const showTranslate = state === "missing" || state === "stale";
                const showAccept = state === "stale";
                return (
                  <div
                    key={`${field}-${locale}`}
                    className="min-w-0 max-w-full overflow-hidden border rounded p-3 bg-gray-50 dark:bg-surface-800"
                  >
                    <div className="flex flex-wrap items-center gap-2 mb-2 min-w-0">
                      <Typography variant="label" className="font-medium truncate">
                        {LOCALE_LABELS[locale]}
                      </Typography>
                      {state && <StateBadge state={state} />}
                    </div>
                    <div className="min-w-0 max-w-full overflow-hidden">
                      {field === "intro" && text ? (
                        <div className="break-words [overflow-wrap:anywhere] [&_*]:max-w-full [&_*]:break-words">
                          <Markdown source={text} size="small" />
                        </div>
                      ) : (
                        <Typography
                          variant="body2"
                          className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]"
                        >
                          {text || "—"}
                        </Typography>
                      )}
                    </div>
                    {locale !== "en" && (showTranslate || showAccept) && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {showTranslate && (
                          <Button
                            size="small"
                            variant="text"
                            disabled={!!busy}
                            onClick={() =>
                              translate({
                                mode: state === "missing" ? "fill_empty" : "force",
                                fields: [field],
                                locales: [locale as TargetLocale],
                              })
                            }
                          >
                            {state === "missing" ? "Translate" : "Re-translate"}
                          </Button>
                        )}
                        {showAccept && (
                          <Button
                            size="small"
                            variant="text"
                            disabled={!!busy}
                            onClick={() =>
                              acceptAsCurrent({
                                fields: [field],
                                locales: [locale as TargetLocale],
                              })
                            }
                          >
                            Accept as current
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Paper>
        );
      })}
    </div>
  );
}
